"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Site Hazard Analysis",
  description: "We conduct a rigorous assessment of your industrial facility, mapping fire risks, chemical storage, and structural vulnerabilities against international safety standards."
}, {
  number: "02",
  title: "Risk Modeling",
  description: "Our engineers simulate potential failure scenarios using advanced computational fluid dynamics to predict fire behavior and containment efficacy in your specific environment."
}, {
  number: "03",
  title: "System Architecture",
  description: "We design bespoke fire suppression and detection systems tailored to your operational footprint, ensuring compliance with regulatory and insurance underwriting requirements."
}, {
  number: "04",
  title: "Precision Implementation",
  description: "Installation is managed through a phased, controlled rollout, ensuring zero disruption to your critical manufacturing or pharmaceutical production cycles."
}, {
  number: "05",
  title: "Lifecycle Maintenance",
  description: "Continuous monitoring, quarterly performance audits, and adaptive system upgrades ensure your protection remains robust as your facility infrastructure evolves."
}];
function MethodologyStep({
  step,
  index,
  delay
}: {
  step: {
    number: string;
    title: string;
    description: string;
  };
  index: number;
  delay: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className="flex flex-col" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(12px)",
    transition: `opacity 600ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 600ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`
  }}>
      <span className="font-mono text-xs text-primary" style={{
      letterSpacing: "0.06em"
    }}>
        {step.number}
      </span>
      <h3 className="font-default mt-4 text-xl md:text-2xl font-semibold text-foreground" style={{
      letterSpacing: "-0.005em",
      lineHeight: 1.25
    }}>
        {step.title}
      </h3>
      <p className="font-default mt-3 text-sm md:text-base text-muted-foreground" style={{
      lineHeight: 1.6
    }}>
        {step.description}
      </p>
    </div>;
}
export default function Engineering() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="11382" id="the-engineering-process" className="bg-background text-foreground py-28 md:py-36">
      <div className="mx-auto max-w-[1280px] px-6 md:px-8 lg:px-12">
        {}
        <div className="grid grid-cols-12">
          <div ref={headerRef} className="col-span-12 md:col-start-3 md:col-span-8 flex flex-col items-start md:items-center text-left md:text-center" style={{
          opacity: headerVisible ? 1 : 0,
          transform: headerVisible ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 600ms cubic-bezier(0.22, 1, 0.36, 1), transform 600ms cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            <span className="font-default text-xs md:text-sm font-medium uppercase text-primary" style={{
            letterSpacing: "0.14em"
          }}>
              Our Methodology
            </span>
            <h1 className="font-default font-serif mt-6 text-foreground" style={{
            fontSize: "clamp(2.75rem, 5.4vw, 4.75rem)",
            fontWeight: 400,
            letterSpacing: "-0.02em",
            lineHeight: 1.04
          }}>
              A discipline of{" "}
              <em className="not-italic" style={{
              fontStyle: "italic",
              color: "var(--primary)"
            }}>
                engineered
              </em>{" "}
              resilience
            </h1>
            <p className="font-default mt-6 text-foreground/90 max-w-[64ch]" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: 1.55
          }}>
              Bedrock Safety Engineering operates as a technical extension of your facility management, translating complex industrial risk into systematic, reliable fire protection architecture that satisfies both safety mandates and insurance requirements.
            </p>
          </div>
        </div>

        {}
        <div className="mt-24 md:mt-32">
          {}
          <div className="hidden lg:grid grid-cols-5 items-start">
            {steps.map((step, i) => <div key={step.number} data-index={i} className="flex items-start">
                <div className="flex-1 pr-8">
                  <MethodologyStep step={step} index={i} delay={i * 120} />
                </div>
                {i < steps.length - 1 && <div className="mt-4 h-px bg-border self-start" style={{
              width: "64px",
              flexShrink: 0
            }} aria-hidden="true" />}
              </div>)}
          </div>

          {}
          <div className="lg:hidden flex flex-col">
            {steps.map((step, i) => <div key={step.number} data-index={i} className="flex flex-col">
                <div className="py-8 first:pt-0">
                  <MethodologyStep step={step} index={i} delay={i * 120} />
                </div>
                {i < steps.length - 1 && <div className="h-px w-full bg-border" aria-hidden="true" />}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}